import { action, makeObservable, observable, runInAction } from 'mobx'
import {
  AI_AGENT_SETTINGS_COMPILER_TOOLS_MUTATION,
  AI_AGENT_SETTINGS_CONFIG_QUERY,
  AI_AGENT_SETTINGS_HOURS_SOURCE_QUERY,
  AI_AGENT_SETTINGS_UPDATE_MUTATION,
} from 'app/components/ai-agent-settings/graphql'
import type { AiAgentSettingsConfigQuery, UpdateAiAgentLocationConfigInput } from 'app/generated/gql/graphql'
import type { AppointmentType, Location } from 'app/generated/graphql'
import type { AvailabilityRecord } from 'app/lib/aiAgentAvailability'
import { gqlMutation, gqlQuery } from 'app/lib/graphqlClient'
import { opsClient } from 'app/lib/opsUrqlClient'
import { logError } from 'lib/logging'
import type { AppointmentTypeStore } from './AppointmentTypeStore'
import type { LocationStore } from './LocationStore'

type AiAgentSettingsConfig = NonNullable<AiAgentSettingsConfigQuery[`aiAgentConfiguration`]>

export type SchedulingData = {
  providers?: { id: string; displayName?: string | null }[]
  availabilities?: AvailabilityRecord[]
}

/** What the Settings page shows for one location, beside the agent config itself. */
export type AgentOverviewRecords = {
  location?: Location
  appointmentTypes: AppointmentType[]
  /** Super users only: the ops read is closed to everyone else. */
  voiceAgentTools: string[]
}

const OPS_READ = { client: opsClient, suppressErrorToast: true }

/** Agents reads and saves: the agent's config per location, its Settings page and its schedule. */
export class AIAgentStore {
  configsByLocationId = new Map<string, AiAgentSettingsConfig | null>()
  overviews = new Map<string, AgentOverviewRecords>()

  private overviewAbort = new AbortController()
  private schedulingAbort = new AbortController()
  /** One request per location and month, kept for the session. */
  private schedulingMonths = new Map<string, Promise<SchedulingData>>()
  private schedulingInFlight = new Set<string>()

  constructor(
    private readonly locations: LocationStore,
    private readonly appointmentTypes: AppointmentTypeStore,
  ) {
    makeObservable(this, { configsByLocationId: observable, overviews: observable.shallow, reset: action })
  }

  async fetchConfig(locationId: string, requestPolicy?: `network-only`): Promise<AiAgentSettingsConfig | null> {
    const result = await gqlQuery(AI_AGENT_SETTINGS_CONFIG_QUERY, { locationId }, { requestPolicy })
    const config = result.data?.aiAgentConfiguration ?? null
    runInAction(() => this.configsByLocationId.set(locationId, config))
    return config
  }

  async locationIdsWithAgent(locationIds: string[]): Promise<Set<string>> {
    const configs = await Promise.all(locationIds.map((locationId) => this.fetchConfig(locationId)))
    return new Set(locationIds.filter((_, index) => configs[index]?.id))
  }

  /** Reads everything the Settings page shows for a location. Starting again drops the previous read. */
  async loadOverview(locationId: string, isSuper: boolean): Promise<void> {
    this.abortOverview()
    const { signal } = this.overviewAbort
    const records = await this.readOverview(locationId, isSuper).catch((error) => {
      logError(`AI Agent Settings: could not load the location or its appointment types`, error)
      return { appointmentTypes: [], voiceAgentTools: [] }
    })
    if (!signal.aborted) runInAction(() => this.overviews.set(locationId, records))
  }

  abortOverview() {
    this.overviewAbort.abort()
    this.overviewAbort = new AbortController()
  }

  private async readOverview(locationId: string, isSuper: boolean): Promise<AgentOverviewRecords> {
    const [, locationResult, typesResult, voiceAgentTools] = await Promise.all([
      this.fetchConfig(locationId, `network-only`),
      this.locations.fetchOne(locationId),
      this.appointmentTypes.fetchList({ locationId }),
      isSuper ? this.fetchVoiceAgentTools(locationId) : [],
    ])
    const location = (locationResult.data as { location?: Location } | undefined)?.location
    const types = (typesResult.data as { appointmentTypes?: { hits?: AppointmentType[] } } | undefined)
      ?.appointmentTypes
    return { location, appointmentTypes: types?.hits ?? [], voiceAgentTools }
  }

  private async fetchVoiceAgentTools(locationId: string): Promise<string[]> {
    const result = await gqlQuery(AI_AGENT_SETTINGS_HOURS_SOURCE_QUERY, { locationId }, OPS_READ)
    return result.data?.aiAgentConfigurations[0]?.voiceAgentTools ?? []
  }

  /** The fields a practice can save. Throws when the API refuses. */
  async updateConfig(locationId: string, input: UpdateAiAgentLocationConfigInput): Promise<void> {
    const result = await gqlMutation(AI_AGENT_SETTINGS_UPDATE_MUTATION, { locationId, input })
    if (result.error) throw new Error(result.error.message)
  }

  /** Throws when the API refuses. */
  async updateVoiceAgentTools(locationId: string, voiceAgentTools: string[]): Promise<void> {
    const result = await gqlMutation(
      AI_AGENT_SETTINGS_COMPILER_TOOLS_MUTATION,
      { locationId, input: { voiceAgentTools } },
      { client: opsClient },
    )
    if (result.error) throw new Error(result.error.message)
  }

  /** The `scheduling-data` month holding `day`. A failed or aborted month is asked for again next time. */
  schedulingMonth(locationId: string, day: string): Promise<SchedulingData> {
    const key = `${locationId}|${day}`
    const cached = this.schedulingMonths.get(key)
    if (cached) return cached
    const inFlight = this.schedulingInFlight
    const request = fetch(`/api/scheduling/locations/${encodeURIComponent(locationId)}/scheduling-data?day=${day}`, {
      credentials: `include`,
      signal: this.schedulingAbort.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error(`scheduling-data responded ${response.status}`)
        return response.json() as Promise<SchedulingData>
      })
      .finally(() => inFlight.delete(key))
    inFlight.add(key)
    this.schedulingMonths.set(key, request)
    request.catch(() => {
      if (this.schedulingMonths.get(key) === request) this.schedulingMonths.delete(key)
    })
    return request
  }

  /** Stops the months still loading; their requests reject with an `AbortError`. */
  abortScheduling() {
    this.schedulingAbort.abort()
    this.schedulingAbort = new AbortController()
    for (const key of this.schedulingInFlight) this.schedulingMonths.delete(key)
    this.schedulingInFlight = new Set()
  }

  reset() {
    this.abortOverview()
    this.abortScheduling()
    this.configsByLocationId.clear()
    this.overviews.clear()
    this.schedulingMonths.clear()
  }
}
